"use client";

import { c as _c } from "react/compiler-runtime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import East from "@mui/icons-material/East";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslations } from "next-intl";
import { JSX, useCallback, useRef, useEffect, useState } from "react";
import { trackVWOEvent } from "@/app/actions/vwo";
import { useAppContext } from "@/app/contextProviders/app-context";
import { VISIT_REASON_PATHS } from "@/constants";
import { DigitalVisitReasonEnum } from "@/lib/__generated/api";
import { useSchedulerActor } from "@/lib/hooks/useSchedulerMachine";
import { useStepActions } from "@/lib/step-actions-context";
import { getSessionId } from "@/lib/utils/user-session-id";
import { BrokenTooth } from "./BrokenTooth";
import { CheckUp } from "./CheckUp";
import { DentalImplants } from "./DentalImplants";
import { Dentures } from "./Dentures";
import { Emergency } from "./Emergency";
import { MottoAligners } from "./MottoAligners";
import styles from "./ReasonForVisit.module.css";
import styles2 from "./ReasonForVisit2.module.css";
type ReasonForVisitOption = {
  label: string;
  value: string;
  key: DigitalVisitReasonEnum;
  icon: JSX.Element;
};
type ReasonForVisitStaticData = Omit<ReasonForVisitOption, "icon">;

// Static data only — NO JSX icons here. Safe to use at module level.
const REASON_FOR_VISIT_DATA: ReasonForVisitStaticData[] = [{
  label: "Emergency",
  value: "emergency",
  key: DigitalVisitReasonEnum.Emergency
}, {
  label: "Broken Tooth/Tooth Pain",
  value: "brokentooth-toothpain",
  key: DigitalVisitReasonEnum.BrokenToothToothPain
}, {
  label: "Dental Implants",
  value: "implants",
  key: DigitalVisitReasonEnum.Implants
}, {
  label: "Dentures",
  value: "dentures",
  key: DigitalVisitReasonEnum.Dentures
}, {
  label: "Motto™ Clear Aligners",
  value: "mottoaligners",
  key: DigitalVisitReasonEnum.ClearAligners
}, {
  label: "Other/Check Up",
  value: "other",
  key: DigitalVisitReasonEnum.Other
}];
const ICONS: Partial<Record<DigitalVisitReasonEnum, JSX.Element>> = {
  [DigitalVisitReasonEnum.Dentures]: <Dentures />,
  [DigitalVisitReasonEnum.Implants]: <DentalImplants />,
  [DigitalVisitReasonEnum.ClearAligners]: <MottoAligners />,
  [DigitalVisitReasonEnum.Emergency]: <Emergency />,
  [DigitalVisitReasonEnum.BrokenToothToothPain]: <BrokenTooth />,
  [DigitalVisitReasonEnum.Other]: <CheckUp />
};

// Called inside components only — safe for JSX/icons.
export const REASON_FOR_VISITS = (t: (key: string) => string): ReasonForVisitOption[] => REASON_FOR_VISIT_DATA.map(item => {
  return {
    ...item,
    label: t(`${item.value}`),
    icon: ICONS[item.key]!
  };
});

// Safe: uses static data with no JSX.
export const REASON_FOR_VISIT_ID: Record<DigitalVisitReasonEnum, ReasonForVisitStaticData> = {
  ...REASON_FOR_VISIT_DATA.reduce<Record<string, ReasonForVisitStaticData>>((map, item) => {
    map[item.key] = item;
    return map;
  }, {}),
  [DigitalVisitReasonEnum.HealthyMouthMovement]: {
    label: "Day of Service",
    value: VISIT_REASON_PATHS[DigitalVisitReasonEnum.HealthyMouthMovement]?.[0] ?? "dayofservice",
    key: DigitalVisitReasonEnum.HealthyMouthMovement
  }
} as Record<DigitalVisitReasonEnum, ReasonForVisitStaticData>;
interface ReasonForVisitItemProps {
  item: ReasonForVisitOption;
  actorRef: ReturnType<typeof useSchedulerActor>;
}
function ReasonForVisitItem(t0) {
  const $ = _c(16);
  const {
    item,
    actorRef
  } = t0;
  const t1 = `reason-for-visit-${item.value}`;
  let t2;
  if ($[0] !== actorRef || $[1] !== item.key) {
    t2 = () => {
      actorRef.send({
        type: "SELECT_REASON",
        reason: item.key
      });
      trackVWOEvent("schedulerStepComplete", getSessionId(), {
        stepName: "reasonForVisit"
      });
    };
    $[0] = actorRef;
    $[1] = item.key;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== item.icon) {
    t3 = <Box aria-hidden="true">{item.icon}</Box>;
    $[3] = item.icon;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      color: "text.secondary"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== item.label) {
    t5 = <Typography variant="bodySSemiBold" sx={t4}>{item.label}</Typography>;
    $[6] = item.label;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <Box className={styles.itemLeft}>{t3}{t5}</Box>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <East className={styles.arrowIcon} />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t6) {
    t8 = <Button className={styles.item} data-testid={t1} onClick={t2}>{t6}{t7}</Button>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t6;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
interface ReasonForVisitGridItemProps {
  item: ReasonForVisitOption;
  isSelected: boolean;
  onSelect: () => void;
}
function ReasonForVisitGridItem(t0) {
  const $ = _c(29);
  const {
    item,
    isSelected,
    onSelect
  } = t0;
  const t1 = `rfv-${item.value}`;
  const t2 = `reason-for-visit-${item.value}`;
  const t3 = isSelected ? `${styles2.item} ${styles2.selected}` : styles2.item;
  const t4 = `rfv-${item.value}`;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      position: "absolute",
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: "hidden",
      clip: "rect(0,0,0,0)",
      whiteSpace: "nowrap",
      border: 0
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  let t6;
  if ($[1] !== isSelected || $[2] !== item.key || $[3] !== item.label || $[4] !== onSelect || $[5] !== t4) {
    t6 = <input type="radio" id={t4} name="reason-for-visit" value={item.key} checked={isSelected} onChange={onSelect} aria-label={item.label} style={t5} />;
    $[1] = isSelected;
    $[2] = item.key;
    $[3] = item.label;
    $[4] = onSelect;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isSelected) {
    t7 = isSelected && <CheckCircleIcon className={styles2.checkIcon} />;
    $[7] = isSelected;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t7) {
    t8 = <Box className={styles2.checkIconWrapper} aria-hidden="true">{t7}</Box>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== item.icon) {
    t9 = <Box aria-hidden="true">{item.icon}</Box>;
    $[11] = item.icon;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      color: "text.secondary"
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== item.label) {
    t11 = <Typography variant="bodySSemiBold" sx={t10}>{item.label}</Typography>;
    $[14] = item.label;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t9) {
    t12 = <Box className={styles2.itemLeft}>{t9}{t11}</Box>;
    $[16] = t11;
    $[17] = t9;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t8) {
    t13 = <Box className={styles2.itemContent}>{t8}{t12}</Box>;
    $[19] = t12;
    $[20] = t8;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== item.label || $[23] !== t1 || $[24] !== t13 || $[25] !== t2 || $[26] !== t3 || $[27] !== t6) {
    t14 = <label htmlFor={t1} data-testid={t2} aria-label={item.label} className={t3}>{t6}{t13}</label>;
    $[22] = item.label;
    $[23] = t1;
    $[24] = t13;
    $[25] = t2;
    $[26] = t3;
    $[27] = t6;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}
export default function ReasonForVisit() {
  const $ = _c(40);
  const actorRef = useSchedulerActor();
  const t = useTranslations("ReasonForVisit");
  const {
    registerContinueHandler,
    unregisterContinueHandler
  } = useStepActions();
  const [selectedKey, setSelectedKey] = useState(null);
  const selectedKeyRef = useRef(null);
  const [showError, setShowError] = useState(false);
  const {
    featureFlags
  } = useAppContext();
  let t0;
  if ($[0] !== actorRef) {
    t0 = () => {
      if (!selectedKeyRef.current) {
        setShowError(true);
        return;
      }
      trackVWOEvent("schedulerStepComplete", getSessionId(), {
        stepName: "reasonForVisit"
      });
      actorRef.send({
        type: "SELECT_REASON",
        reason: selectedKeyRef.current
      });
    };
    $[0] = actorRef;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onContinue = t0;
  let t1;
  let t2;
  if ($[2] !== onContinue || $[3] !== registerContinueHandler || $[4] !== unregisterContinueHandler) {
    t1 = () => {
      registerContinueHandler(onContinue);
      return unregisterContinueHandler;
    };
    t2 = [registerContinueHandler, unregisterContinueHandler, onContinue];
    $[2] = onContinue;
    $[3] = registerContinueHandler;
    $[4] = unregisterContinueHandler;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  if (featureFlags.disableGridLayout) {
    let t3;
    if ($[7] !== t) {
      t3 = t("title");
      $[7] = t;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== t3) {
      t4 = <Typography id="reason-for-visit-title" data-testid="reason-for-visit-heading" variant="headingL" component="h1">{t3}</Typography>;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== actorRef || $[12] !== t) {
      let t6;
      if ($[14] !== actorRef) {
        t6 = item => <ReasonForVisitItem key={item.value} item={item} actorRef={actorRef} />;
        $[14] = actorRef;
        $[15] = t6;
      } else {
        t6 = $[15];
      }
      t5 = REASON_FOR_VISITS(t).map(t6);
      $[11] = actorRef;
      $[12] = t;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[16] !== t5) {
      t6 = <Stack spacing={1}>{t5}</Stack>;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== t4 || $[19] !== t6) {
      t7 = <Box className={styles.root} aria-labelledby="reason-for-visit-title">{t4}{t6}</Box>;
      $[18] = t4;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  let t3;
  if ($[21] !== onContinue) {
    t3 = e => {
      if (e.key === "Enter") {
        e.preventDefault();
        onContinue();
      }
    };
    $[21] = onContinue;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== t) {
    t4 = t("title");
    $[23] = t;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== t4) {
    t5 = <Typography component="legend" data-testid="reason-for-visit-heading" variant="headingL" className={styles2.legend}>{t4}</Typography>;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== selectedKey || $[28] !== t) {
    t6 = REASON_FOR_VISITS(t).map(item_0 => <ReasonForVisitGridItem key={item_0.value} item={item_0} isSelected={selectedKey === item_0.key} onSelect={() => {
      selectedKeyRef.current = item_0.key;
      setSelectedKey(item_0.key);
      setShowError(false);
    }} />);
    $[27] = selectedKey;
    $[28] = t;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== t6) {
    t7 = <Box className={styles2.gridContainer}>{t6}</Box>;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== showError || $[33] !== t) {
    t8 = showError && <Typography role="alert" variant="bodyS" className={styles2.errorText}>{t("required")}</Typography>;
    $[32] = showError;
    $[33] = t;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t3 || $[36] !== t5 || $[37] !== t7 || $[38] !== t8) {
    t9 = <Box component="fieldset" className={styles2.root} onKeyDown={t3}>{t5}{t7}{t8}</Box>;
    $[35] = t3;
    $[36] = t5;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}